import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { RefreshCw, Zap, Info, ChevronDown, Award, Search, X, ArrowRightLeft } from 'lucide-react';
import WalletButton from './WalletButton';

const ChainLogo = ({ chain }) => {
  const logoUrls = {
    'ETHEREUM': 'https://cryptologos.cc/logos/ethereum-eth-logo.png',
    'WETH': 'https://cryptologos.cc/logos/ethereum-eth-logo.png',
    'ARBITRUM': 'https://cryptologos.cc/logos/arbitrum-arb-logo.png',
    'OPTIMISM': 'https://cryptologos.cc/logos/optimism-ethereum-op-logo.png',
    'BASE': 'https://altcoinsbox.com/wp-content/uploads/2023/02/base-logo-in-blue.png'
  };
  
  const url = logoUrls[chain];
  if (!url) return null;
  
  return (
    <img 
      src={url} 
      alt={chain} 
      className="w-full h-full object-contain"
      style={{ 
        imageRendering: 'crisp-edges',
        mixBlendMode: 'normal',
        backgroundColor: 'transparent'
      }}
      onError={(e) => {
        e.target.style.display = 'none';
      }}
    />
  );
};

const CryptoTracker = () => {
  const location = useLocation();
  const [tokenAmount, setTokenAmount] = useState(1);
  const [selectedToken, setSelectedToken] = useState('ETH');
  const [lastUpdate, setLastUpdate] = useState(new Date());
  const [selectedChain, setSelectedChain] = useState('ALL');
  const [expandedDexes, setExpandedDexes] = useState({});
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [prices, setPrices] = useState(null);
  const [gasPrices, setGasPrices] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [availableTokens, setAvailableTokens] = useState({});
  const [tokenChange24h, setTokenChange24h] = useState(0);
  
  const [showTokenSearch, setShowTokenSearch] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [isNavigating, setIsNavigating] = useState(false);
  const searchRef = useRef(null);

  const chains = ['ETHEREUM', 'ARBITRUM', 'OPTIMISM', 'BASE'];

  const chainColors = {
    ETHEREUM: 'from-purple-500 to-purple-600',
    ARBITRUM: 'from-blue-500 to-blue-600',
    OPTIMISM: 'from-red-500 to-red-600',
    BASE: 'from-indigo-500 to-indigo-600'
  };

  useEffect(() => {
    fetchAvailableTokens();
  }, []);

  useEffect(() => {
    if (selectedToken) {
      fetchPrices();
    }
  }, [selectedToken]);

  useEffect(() => {
    const interval = setInterval(fetchPrices, 30000);
    return () => clearInterval(interval);
  }, [selectedToken]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        setShowTokenSearch(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const fetchAvailableTokens = async () => {
    try {
      const response = await fetch('/api/tokens');
      if (!response.ok) throw new Error('Failed to fetch tokens');
      const data = await response.json();
      setAvailableTokens(data.tokens);
    } catch (err) {
      console.error('Error fetching tokens:', err);
    }
  };

  const fetchPrices = async () => {
    try {
      setError(null);
      const response = await fetch(`/api/prices/${selectedToken}`, {
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) throw new Error('Failed to fetch prices');
      const data = await response.json();
      setPrices(data.prices);
      setGasPrices(data.gasPrices);
      setTokenChange24h(data.change_24h || 0);
      setLastUpdate(new Date());
      setLoading(false);
    } catch (err) {
      console.error('Error fetching prices:', err);
      setError('Limit exceeded. Please refresh the browser to continue.');
      setLoading(false);
    }
  };

  const calculateSwap = (price, fee, chain) => {
    const grossOutput = tokenAmount * price;
    const feeAmount = grossOutput * (fee / 100);
    const gasPrice = gasPrices[chain] || 0;
    
    let gasCostUSD = 0;
    if (chain === 'ETHEREUM') {
      gasCostUSD = (gasPrice * 180000) / 1e9 * 3000;
    } else {
      gasCostUSD = (gasPrice * 1000000) / 1e9 * 3000 * 0.001;
    }
    
    const netOutput = grossOutput - feeAmount - gasCostUSD;
    
    return { grossOutput, feeAmount, gasCostUSD, netOutput };
  };

  const getBestDeal = () => {
    if (!prices) return { dex: '', chain: '', netOutput: 0 };
    
    let best = { dex: '', chain: '', netOutput: 0 };
    
    Object.keys(prices).forEach(dex => {
      chains.forEach(chain => {
        const data = prices[dex][chain];
        if (data.available && data.price) {
          const swap = calculateSwap(data.price, data.fee, chain);
          if (swap.netOutput > best.netOutput) {
            best = { dex, chain, netOutput: swap.netOutput, ...swap, price: data.price, fee: data.fee };
          }
        }
      });
    });
    
    return best;
  };

  const handleRefresh = () => {
    setIsRefreshing(true);
    fetchPrices();
    setTimeout(() => setIsRefreshing(false), 1000);
  };

  const toggleDex = (dexName) => {
    setExpandedDexes(prev => ({
      ...prev,
      [dexName]: !prev[dexName]
    }));
  };

  const handleTokenSelect = (tokenSymbol) => {
    setSelectedToken(tokenSymbol);
    setShowTokenSearch(false);
    setSearchQuery('');
  };

  const filteredTokens = Object.entries(availableTokens).filter(([symbol, token]) => {
    const query = searchQuery.toLowerCase();
    return token.name.toLowerCase().includes(query) || 
           token.symbol.toLowerCase().includes(query);
  });

  const currentToken = availableTokens[selectedToken] || { name: 'Ethereum', symbol: 'ETH', logo: '' };

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-center">
          <RefreshCw className="w-12 h-12 text-[#476bff] animate-spin mx-auto mb-4" />
          <p className="text-gray-400">Loading live prices...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center p-4">
        <div className="bg-gradient-to-br from-gray-900 to-black rounded-2xl shadow-2xl p-8 max-w-md border border-gray-800">
          <div className="text-red-500 text-5xl mb-4">⚠️</div>
          <h2 className="text-2xl font-bold text-white mb-2">Connection Error (Rate Limited)</h2>
          <p className="text-gray-400 mb-4">{error}</p>
          <p className="text-sm text-gray-500 mb-4">
            Please refresh your browser:<br/>
            <code className="bg-gray-800 px-2 py-1 rounded text-[#50ffff]">Ctrl + Shift + R / ⌘ + Shift + R</code>
          </p>
          <button
            onClick={fetchPrices}
            className="w-full bg-gradient-to-r from-[#476bff] to-[#50ffff] hover:opacity-90 text-white rounded-xl px-4 py-3 flex items-center justify-center gap-2 transition font-bold"
          >
            <RefreshCw className="w-4 h-4" />
            Retry Connection
          </button>
        </div>
      </div>
    );
  }

  const bestDeal = getBestDeal();
  const filteredChains = selectedChain === 'ALL' ? chains : [selectedChain];

  const dexes = prices ? Object.keys(prices)
    .map(name => {
      let icon, color, order;
      
      if (name.includes('1inch')) {
        icon = (
          <img 
            src="https://cryptologos.cc/logos/1inch-1inch-logo.png" 
            alt="1inch" 
            className="w-10 h-10 object-contain"
            onError={(e) => {
              e.target.onerror = null;
              e.target.src = "https://s2.coinmarketcap.com/static/img/coins/64x64/8104.png";
            }}
          />
        );
        color = 'from-blue-500 to-cyan-500';
        order = 100;
      } else if (name.includes('Uniswap')) {
        icon = '🦄';
        color = 'from-pink-500 to-pink-600';
        order = 101;
      } else if (name.includes('Sushi')) {
        icon = '🍣';
        color = 'from-blue-500 to-blue-600';
        order = 102;
      } else if (name.includes('Pancake')) {
        icon = '🥞';
        color = 'from-yellow-500 to-yellow-600';
        order = 103;
      } else if (name.includes('Aerodrome')) {
        icon = '✈️';
        color = 'from-indigo-500 to-indigo-600';
        order = 104;
      } else {
        const chainName = name.split(' ')[0].toUpperCase();
        icon = (
          <div className="w-10 h-10 flex items-center justify-center">
            <ChainLogo chain={chainName} />
          </div>
        );
        color = 'from-gray-700 to-gray-800';
        
        if (chainName === 'ETHEREUM' || chainName === 'WETH') {
          order = 0;
        } else if (chainName === 'ARBITRUM') {
          order = 1;
        } else if (chainName === 'OPTIMISM') {
          order = 2;
        } else if (chainName === 'BASE') {
          order = 3;
        } else {
          order = 99;
        }
      }
      
      let displayName = name;
      if (displayName === 'Weth') {
        displayName = 'Ethereum Weth';
      }
      
      return { name, displayName, icon, color, order };
    })
    .sort((a, b) => a.order - b.order) : [];

  return (
    <div className="min-h-screen bg-black p-4 md:p-8">
      <div className="max-w-7xl mx-auto">
        
        <div className="fixed top-4 right-4 z-50">
          <WalletButton />
        </div>

        <div className="text-center mb-8">
          <div className="flex items-center justify-center gap-4 mb-1 -mt-8 md:-mt-12">
            <img 
              src="/logo.png" 
              alt="PrecogNexus" 
              className="h-[120px] md:h-[200px] w-auto drop-shadow-[0_0_20px_rgba(80,255,255,0.4)]" 
            />
          </div>
          <div className="inline-flex items-center gap-2 bg-gradient-to-r from-gray-900 to-gray-800 rounded-full px-4 py-2 shadow-lg mb-4 border border-gray-700">
            <Zap className="w-4 h-4 text-[#c5d434]" />
            <span className="text-sm font-bold text-white tracking-wide" style={{fontFamily: '"Walkway Expand", sans-serif'}}>LIVE PRICE TRACKER</span>
          </div>
          <h1 className="text-4xl md:text-6xl font-black text-white mb-2" style={{fontFamily: '"Walkway Expand", sans-serif', letterSpacing: '0.05em'}}>
            DEX PRICES
          </h1>
          <p className="text-gray-400 text-lg mb-4">Compare rates across multiple chains instantly</p>
          
          <div className="flex items-center justify-center gap-4 flex-wrap">
            <Link 
              to="/multi-coin"
              className="inline-flex items-center gap-2 bg-gradient-to-r from-[#c5d434] to-[#50ffff] hover:opacity-90 text-black rounded-xl px-6 py-3 font-bold transition shadow-lg"
            >
              Multi-Coin Deal Finder
            </Link>
            
            
            <Link 
              to="/swap"
              onClick={(e) => {
                e.preventDefault();
                setIsNavigating(true);
                setTimeout(() => {
                  window.location.href = '/swap';
                }, 800);
              }}
              className="inline-flex items-center gap-2 bg-gradient-to-r from-[#476bff] to-[#50ffff] hover:opacity-90 text-white rounded-xl px-6 py-3 font-bold transition shadow-lg"
            >
              <ArrowRightLeft className="w-5 h-5" />
              Swap Tokens
            </Link>
          </div>
        </div>

        <div className="bg-gradient-to-br from-gray-900 to-black rounded-2xl shadow-2xl p-6 mb-6 border border-gray-800">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
            <div ref={searchRef} className="relative">
              <label className="block text-sm font-bold text-[#50ffff] mb-2 tracking-wide" style={{fontFamily: '"Walkway Expand", sans-serif'}}>
                SELECT TOKEN
              </label>
              <button
                onClick={() => setShowTokenSearch(!showTokenSearch)}
                className="w-full bg-black border-2 border-gray-700 rounded-xl px-4 py-3 text-white focus:ring-2 focus:ring-[#476bff] focus:border-[#476bff] outline-none transition flex items-center justify-between hover:border-[#476bff] h-[60px]"
              >
                <div className="flex items-center gap-2">
                  {currentToken.logo ? (
                    <img src={currentToken.logo} alt={currentToken.symbol} className="w-8 h-8 rounded-full" />
                  ) : (
                    <div className="w-8 h-8 rounded-full bg-gray-700"></div>
                  )}
                  <div className="text-left">
                    <p className="font-bold">{currentToken.symbol}</p>
                    <p className="text-xs text-gray-500">{currentToken.name}</p>
                  </div>
                </div>
                <ChevronDown className={`w-4 h-4 text-[#50ffff] transition-transform ${showTokenSearch ? 'rotate-180' : ''}`} />
              </button>

              {showTokenSearch && (
                <div className="absolute top-full left-0 right-0 mt-2 bg-gray-900 border-2 border-gray-700 rounded-xl shadow-2xl z-50 max-h-96 overflow-hidden">
                  <div className="p-3 border-b border-gray-700">
                    <div className="relative">
                      <Search className="absolute left-3 top-3 w-4 h-4 text-gray-500" />
                      <input
                        type="text"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        placeholder="Search tokens..."
                        className="w-full bg-black border border-gray-700 rounded-lg pl-10 pr-4 py-2 text-white text-sm focus:ring-2 focus:ring-[#476bff] outline-none"
                        autoFocus
                      />
                      {searchQuery && (
                        <button
                          onClick={() => setSearchQuery('')}
                          className="absolute right-3 top-2.5"
                        >
                          <X className="w-4 h-4 text-gray-500 hover:text-white" />
                        </button>
                      )}
                    </div>
                  </div>
                  <div className="max-h-64 overflow-y-auto">
                    {filteredTokens.map(([symbol, token]) => (
                      <button
                        key={symbol}
                        onClick={() => handleTokenSelect(symbol)}
                        className={`w-full px-4 py-3 flex items-center gap-3 hover:bg-gray-800 transition ${selectedToken === symbol ? 'bg-gray-800 border-l-4 border-[#476bff]' : ''}`}
                      >
                        {token.logo ? (
                          <img src={token.logo} alt={token.symbol} className="w-8 h-8 rounded-full" />
                        ) : (
                          <div className="w-8 h-8 rounded-full bg-gray-700"></div>
                        )}
                        <div className="text-left flex-1">
                          <p className="font-bold text-white">{token.symbol}</p>
                          <p className="text-xs text-gray-500">{token.name}</p>
                        </div>
                        {selectedToken === symbol && (
                          <div className="w-2 h-2 rounded-full bg-[#c5d434]"></div>
                        )}
                      </button>
                    ))}
                    {filteredTokens.length === 0 && (
                      <div className="px-4 py-8 text-center text-gray-500">
                        No tokens found
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>

            <div>
              <label className="block text-sm font-bold text-[#50ffff] mb-2 tracking-wide" style={{fontFamily: '"Walkway Expand", sans-serif'}}>
                AMOUNT
              </label>
              <div className="relative">
                <input
                  type="number"
                  value={tokenAmount}
                  onChange={(e) => setTokenAmount(parseFloat(e.target.value) || 0)}
                  className="w-full bg-black border-2 border-gray-700 rounded-xl px-4 py-3 text-white focus:ring-2 focus:ring-[#476bff] focus:border-[#476bff] outline-none transition h-[60px]"
                  placeholder="1.0"
                  step="0.1"
                  min="0"
                />
                <Zap className="absolute right-3 top-[18px] w-5 h-5 text-[#c5d434]" />
              </div>
            </div>

            <div>
              <label className="block text-sm font-bold text-[#50ffff] mb-2 tracking-wide" style={{fontFamily: '"Walkway Expand", sans-serif'}}>
                ACTIONS
              </label>
              <button
                onClick={handleRefresh}
                disabled={isRefreshing}
                className="w-full bg-gradient-to-r from-[#476bff] to-[#50ffff] hover:opacity-90 text-white rounded-xl px-4 py-3 flex items-center justify-center gap-2 transition shadow-lg font-bold disabled:opacity-70 h-[60px]"
                style={{fontFamily: '"Walkway Expand", sans-serif'}}
              >
                <RefreshCw className={`w-4 h-4 ${isRefreshing ? 'animate-spin' : ''}`} />
                <span className="text-sm">REFRESH</span>
              </button>
            </div>
          </div>

          <div className="border-t border-gray-800 pt-6">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div className="flex-1">
                <label className="block text-sm font-bold text-[#50ffff] mb-3 tracking-wide" style={{fontFamily: '"Walkway Expand", sans-serif'}}>
                  FILTER CHAIN
                </label>
                <div className="flex flex-wrap gap-2">
                  <button
                    onClick={() => setSelectedChain('ALL')}
                    className={`px-4 py-2 rounded-lg font-bold text-sm transition-all ${
                      selectedChain === 'ALL'
                        ? 'bg-gradient-to-r from-[#476bff] to-[#50ffff] text-white shadow-lg scale-105'
                        : 'bg-black border-2 border-gray-700 text-gray-400 hover:border-[#476bff] hover:text-white'
                    }`}
                    style={{fontFamily: '"Walkway Expand", sans-serif'}}
                  >
                    ALL CHAINS
                  </button>
                  {chains.map(chain => (
                    <button
                      key={chain}
                      onClick={() => setSelectedChain(chain)}
                      className={`px-4 py-2 rounded-lg font-bold text-sm transition-all flex items-center gap-2 ${
                        selectedChain === chain
                          ? `bg-gradient-to-r ${chainColors[chain]} text-white shadow-lg scale-105`
                          : 'bg-black border-2 border-gray-700 text-gray-400 hover:border-[#476bff] hover:text-white'
                      }`}
                      style={{fontFamily: '"Walkway Expand", sans-serif'}}
                    >
                      <div className={`w-2 h-2 rounded-full ${selectedChain === chain ? 'bg-white' : `bg-gradient-to-r ${chainColors[chain]}`}`}></div>
                      {chain}
                    </button>
                  ))}
                </div>
              </div>

              <div className="flex items-center gap-4 sm:self-end pb-1">
                <div className="flex items-center gap-2">
                  <span className="text-xs text-gray-500">Last updated:</span>
                  <span className="text-xs text-gray-400 font-semibold">{lastUpdate.toLocaleTimeString()}</span>
                </div>
                {tokenChange24h !== 0 && (
                  <>
                    <div className="w-px h-4 bg-gray-700"></div>
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-gray-500">24h:</span>
                      <span className={`text-sm font-bold ${tokenChange24h >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                        {tokenChange24h >= 0 ? '↑' : '↓'} {Math.abs(tokenChange24h).toFixed(2)}%
                      </span>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>

        {bestDeal.netOutput > 0 && (
          <div className="bg-gradient-to-r from-[#c5d434] to-[#50ffff] rounded-2xl shadow-2xl p-6 mb-6 text-black border-2 border-[#c5d434]">
            <div className="flex items-center justify-between flex-wrap gap-4">
              <div className="flex items-center gap-4">
                <div className="bg-black/20 backdrop-blur-sm rounded-xl p-3">
                  <Award className="w-8 h-8" />
                </div>
                <div>
                  <p className="text-black/80 text-sm font-bold mb-1" style={{fontFamily: '"Walkway Expand", sans-serif'}}>BEST DEAL</p>
                  <p className="text-3xl font-black" style={{fontFamily: '"Walkway Expand", sans-serif'}}>{bestDeal.dex}</p>
                  <p className="text-black/90 text-sm font-semibold">{bestDeal.chain}</p>
                </div>
              </div>
              <div className="text-right">
                <p className="text-black/80 text-sm font-bold mb-1" style={{fontFamily: '"Walkway Expand", sans-serif'}}>YOU RECEIVE</p>
                <p className="text-4xl font-black" style={{fontFamily: '"Walkway Expand", sans-serif'}}>
                  ${bestDeal.netOutput.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}
                </p>
                <p className="text-black/70 text-xs mt-1 font-semibold">
                  Fee: ${bestDeal.feeAmount.toFixed(2)} · Gas: ${bestDeal.gasCostUSD.toFixed(2)}
                </p>
              </div>
            </div>
          </div>
        )}

        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
          {chains.map(chain => (
            <div key={chain} className="bg-gradient-to-br from-gray-900 to-black rounded-xl shadow-lg p-4 border border-gray-800">
              <div className="flex items-center gap-2 mb-2">
                <div className={`w-2 h-2 rounded-full bg-gradient-to-r ${chainColors[chain]}`}></div>
                <p className="text-xs font-bold text-[#50ffff]" style={{fontFamily: '"Walkway Expand", sans-serif'}}>{chain}</p>
              </div>
              <p className="text-2xl font-black text-white">
                {gasPrices[chain] !== undefined ? gasPrices[chain].toFixed(4) : 'N/A'}
              </p>
              <p className="text-xs text-gray-500 font-semibold">Gwei</p>
            </div>
          ))}
        </div>

        <div className="space-y-4">
          {dexes.map(dex => {
            const isExpanded = expandedDexes[dex.name];
            const availableChains = filteredChains.filter(chain => 
              prices && prices[dex.name] && prices[dex.name][chain] && prices[dex.name][chain].available
            );
            
            return (
              <div key={dex.name} className="bg-gradient-to-br from-gray-900 to-black rounded-2xl shadow-2xl overflow-hidden border border-gray-800">
                <button
                  onClick={() => toggleDex(dex.name)}
                  className="w-full p-6 flex items-center justify-between hover:bg-gray-800/50 transition"
                >
                  <div className="flex items-center gap-4">
                    <div className={`bg-gradient-to-br ${dex.color} w-14 h-14 rounded-xl flex items-center justify-center text-3xl shadow-lg`}>
                      {dex.icon}
                    </div>
                    <div className="text-left">
                      <h2 className="text-2xl font-black text-white" style={{fontFamily: '"Walkway Expand", sans-serif'}}>{dex.displayName}</h2>
                      <p className="text-sm text-gray-400 font-semibold">{availableChains.length} chains available</p>
                    </div>
                  </div>
                  <ChevronDown className={`w-5 h-5 text-[#50ffff] transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
                </button>

                {isExpanded && prices && (
                  <div className="px-6 pb-6 border-t border-gray-800">
                    <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4 mt-4">
                      {filteredChains.map(chain => {
                        const data = prices[dex.name][chain];
                        
                        if (!data || !data.available) {
                          return (
                            <div key={chain} className="bg-gray-900/50 rounded-xl p-4 border border-gray-800 opacity-50">
                              <div className="flex items-center gap-2 mb-3">
                                <div className={`w-2 h-2 rounded-full bg-gradient-to-r ${chainColors[chain]}`}></div>
                                <p className="font-bold text-white" style={{fontFamily: '"Walkway Expand", sans-serif'}}>{chain}</p>
                              </div>
                              <p className="text-sm text-gray-500">Not Available</p>
                            </div>
                          );
                        }

                        const swap = calculateSwap(data.price, data.fee, chain);

                        return (
                          <div key={chain} className="bg-gradient-to-br from-gray-800 to-black rounded-xl p-4 border border-gray-700 hover:border-[#476bff] hover:shadow-xl transition">
                            <div className="flex justify-between items-start mb-4">
                              <div className="flex items-center gap-2">
                                <div className={`w-2 h-2 rounded-full bg-gradient-to-r ${chainColors[chain]}`}></div>
                                <p className="font-bold text-white" style={{fontFamily: '"Walkway Expand", sans-serif'}}>{chain}</p>
                              </div>
                              <span className="text-xs font-bold bg-[#476bff] text-white px-2 py-1 rounded-full">
                                {data.fee.toFixed(2)}%
                              </span>
                            </div>
                            
                            <div className="space-y-2">
                              <div className="pb-2 border-b border-gray-700">
                                <p className="text-xs text-gray-400 mb-1 font-semibold">Price</p>
                                <p className="text-xl font-black text-white">${data.price.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}</p>
                              </div>
                              
                              <div className="flex justify-between text-xs">
                                <span className="text-gray-400 font-semibold">Gross</span>
                                <span className="font-bold text-gray-300">${swap.grossOutput.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}</span>
                              </div>
                              
                              <div className="flex justify-between text-xs">
                                <span className="text-red-400 font-semibold">Fee</span>
                                <span className="font-bold text-red-400">-${swap.feeAmount.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}</span>
                              </div>
                              
                              <div className="flex justify-between text-xs">
                                <span className="text-red-400 font-semibold">Gas</span>
                                <span className="font-bold text-red-400">-${swap.gasCostUSD.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}</span>
                              </div>
                              
                              <div className="pt-2 border-t border-gray-700">
                                <div className="flex justify-between items-center">
                                  <span className="text-xs font-bold text-[#50ffff]" style={{fontFamily: '"Walkway Expand", sans-serif'}}>NET OUTPUT</span>
                                  <span className="text-xl font-black text-[#c5d434]">
                                    ${swap.netOutput.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}
                                  </span>
                                </div>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        <div className="mt-6 bg-gradient-to-r from-gray-900 to-black border border-[#476bff] rounded-xl p-4">
          <div className="flex gap-3">
            <Info className="w-5 h-5 text-[#476bff] flex-shrink-0 mt-0.5" />
            <div className="text-sm text-gray-300">
              <p className="font-bold mb-2 text-white" style={{fontFamily: '"Walkway Expand", sans-serif'}}>POWERED BY PRECOGNEXUS:</p>
              <ul className="space-y-1 text-xs">
                <li>• Real-time gas prices from blockchain RPC</li>
                <li>• Gas costs estimated based on typical swap operations</li>
                <li>• Auto-refresh every 30 seconds</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
      {isNavigating && (
        <div className="min-h-screen bg-black flex items-center justify-center fixed inset-0 z-50">
          <div className="text-center">
            <RefreshCw className="w-12 h-12 text-[#476bff] animate-spin mx-auto mb-4" />
            <p className="text-gray-400">Navigating to Swap...</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default CryptoTracker;